/**
 * T2 in-theme navigation — mobile drawer stitcher.
 *
 * Why this exists: the nav is authored ONCE as nested theme blocks
 * (_nav-menu → _nav-group → _nav-link). Shopify renders a block's
 * children exactly once ({% content_for 'blocks' %} may appear only once
 * per file — platform validation), so a single block tree cannot
 * server-render two differently-shaped DOMs (desktop mega menu + mobile
 * drawer). Instead each block server-renders its desktop markup AND an
 * inert <template data-navdrawer="..."> holding its drawer fragment,
 * authored in Liquid with the exact classes/structure of
 * snippets/header-drawer.liquid. This script only STITCHES those
 * fragments together — it generates no markup of its own, so drawer
 * styling/behavior stays byte-compatible with the stock drawer.
 * Horizon's on:click handlers are document-level delegated
 * (assets/component.js), so late-inserted nodes behave identically.
 *
 * Resilience notes (each earned by a real failure):
 *  • The desktop items are slotted light-DOM children of the
 *    <overflow-list> declarative-shadow-DOM component — there is NO
 *    .menu-list__list element in the served DOM, so we anchor on the
 *    <template data-navdrawer="menu"> elements themselves.
 *  • Horizon re-renders the header via the Section Rendering API after
 *    load (two-pass "eager loading"), REPLACING the drawer with a fresh
 *    unstitched copy. So stitching must be re-runnable: we watch for any
 *    pending drawer root via MutationObserver and also re-stitch on
 *    every drawer-open interaction. The per-root "done" marker makes
 *    repeat calls no-ops.
 */
(function () {
  'use strict';

  function stitchRoot(root) {
    var headerMenu = document.querySelector('header-menu');
    if (!headerMenu) return false;

    var menuTpls = headerMenu.querySelectorAll('template[data-navdrawer="menu"]');
    if (!menuTpls.length) return false;

    var animIndex = 0;
    menuTpls.forEach(function (menuTpl) {
      var item = menuTpl.closest('.menu-list__list-item') || menuTpl.parentElement;

      animIndex += 1;
      var li = document.createElement('li');
      li.className = 'menu-drawer__list-item';
      li.style.setProperty('--menu-drawer-animation-index', animIndex);
      li.appendChild(menuTpl.content.cloneNode(true));
      root.appendChild(li);

      var menuSlot = li.querySelector('[data-navdrawer-slot]');
      if (!menuSlot) return;

      // Groups: each desktop mega-menu column owns one group template.
      item.querySelectorAll('.mega-menu__column').forEach(function (col) {
        var groupTpl = null;
        for (var i = 0; i < col.children.length; i++) {
          if (col.children[i].matches('template[data-navdrawer="group"]')) groupTpl = col.children[i];
        }
        if (!groupTpl) return;

        if (groupTpl.hasAttribute('data-headerless')) {
          // Headerless group (no heading, e.g. COLLABORATIONS): its links
          // sit directly in the menu's drawer list as --parent rows, the
          // way the linklist drawer rendered second-level leaf items.
          col.querySelectorAll('template[data-navdrawer="link"]').forEach(function (linkTpl) {
            var frag = linkTpl.content.cloneNode(true);
            var a = frag.querySelector('a');
            if (a) a.className = a.className.replace('menu-drawer__menu-item--child', 'menu-drawer__menu-item--parent');
            menuSlot.appendChild(frag);
          });
          return;
        }

        var groupLi = document.createElement('li');
        groupLi.className = 'menu-drawer__list-item ' +
          (groupTpl.hasAttribute('data-flat') ? 'menu-drawer__list-item--flat' : 'menu-drawer__list-item--deep');
        groupLi.appendChild(groupTpl.content.cloneNode(true));
        menuSlot.appendChild(groupLi);

        var groupSlot = groupLi.querySelector('[data-navdrawer-slot]');
        if (!groupSlot) return;

        var linkIndex = 0;
        col.querySelectorAll('template[data-navdrawer="link"]').forEach(function (linkTpl) {
          linkIndex += 1;
          var frag = linkTpl.content.cloneNode(true);
          var linkLi = frag.querySelector('li');
          if (linkLi) linkLi.style.setProperty('--menu-drawer-animation-index', linkIndex);
          groupSlot.appendChild(frag);
        });
      });
    });

    return true;
  }

  function stitchAll() {
    document.querySelectorAll('[data-navdrawer-root]').forEach(function (root) {
      if (root.getAttribute('data-navdrawer-root') === 'done') return;
      if (stitchRoot(root)) root.setAttribute('data-navdrawer-root', 'done');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', stitchAll);
  } else {
    stitchAll();
  }

  // The header section can be re-rendered (Section Rendering API second
  // pass), replacing a stitched drawer with a fresh pending one. Watch
  // for that and restitch. Cheap: the callback only scans when nodes
  // were added, and stitchAll no-ops on already-done roots.
  var scheduled = false;
  new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      stitchAll();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  // Belt-and-suspenders: any interaction with the drawer trigger gets a
  // synchronous stitch attempt before the drawer becomes visible.
  document.addEventListener('pointerdown', function (e) {
    if (e.target && e.target.closest && e.target.closest('.header__icon--menu, header-drawer')) stitchAll();
  }, true);
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.header__icon--menu, header-drawer')) stitchAll();
  }, true);
})();
